// Общий рендер публичных карточек: используется сервером (SSR) и клиентом (живые обновления).
import { esc, paragraphs, pct, votesWord, participantsWord, answersWord } from './html.ts';
import { feedTime, cardDateTime } from './time.ts';
import type { PublicPost, PollResult, PredictionResult, ScreenQuestion, ScreenState, Result } from './types.ts';
import { TYPE_LABELS } from './types.ts';

export interface CardOptions {
  mode?: 'feed' | 'single' | 'question';
  voted?: { optionId?: string; probability?: number } | null;
  /** Уровень заголовка: 1 — карточка как основное содержимое страницы, по умолчанию 3 (лента). */
  level?: 1 | 3;
}

export function importantBadge(): string {
  return '<span class="hot"><span aria-hidden="true">🔥</span> Важное</span>';
}

export function renderPollResult(r: PollResult, chosen?: string): string {
  return `<ol class="poll-result" aria-label="Результаты опроса">${r.options.map((o) => `
    <li class="poll-result__row${o.id === chosen ? ' is-chosen' : ''}">
      <span class="poll-result__line"><span class="poll-result__label">${esc(o.text)}${o.id === chosen ? ' <span class="poll-result__mine">· ваш ответ</span>' : ''}</span>
      <span class="poll-result__num"><b>${pct(o.share)}</b> ${o.votes}</span></span>
      <span class="poll-result__track" aria-hidden="true"><span class="poll-result__bar" style="--w:${o.share === null ? 0 : (o.share * 100).toFixed(2)}%"></span></span>
    </li>`).join('')}
  </ol><p class="result-total">${r.total ? `Всего ${votesWord(r.total)}` : 'Пока нет голосов'}</p>`;
}

export function renderPredictionResult(r: PredictionResult, mine?: number): string {
  if (r.average === null) {
    return '<div class="pred-result is-empty"><div class="pred-scale"><span class="pred-scale__track"></span></div><p class="result-total">Пока нет ответов</p></div>';
  }
  const avg = Math.round(r.average);
  return `<div class="pred-result">
    <div class="pred-scale" role="img" aria-label="Средняя оценка вероятности ${avg}%">
      <span class="pred-scale__track"><span class="pred-scale__fill" style="--w:${r.average.toFixed(2)}%"></span></span>
      <span class="pred-scale__mark" style="--x:${r.average.toFixed(2)}%"><b>${avg}%</b></span>
      ${mine !== undefined ? `<span class="pred-scale__mine" style="--x:${mine}%" title="Ваша оценка ${mine}%"></span>` : ''}
      <span class="pred-scale__ticks" aria-hidden="true"><i>0</i><i>25</i><i>50</i><i>75</i><i>100</i></span>
    </div>
    <p class="result-total">Средняя оценка вероятности · ${participantsWord(r.participants)}${mine !== undefined ? ` · ваша оценка ${mine}%` : ''}</p>
  </div>`;
}

function renderImage(p: PublicPost): string {
  if (!p.image) return '';
  const cap = p.image.caption ? `<figcaption>${esc(p.image.caption)}</figcaption>` : '';
  return `<figure class="card__figure"><img src="${esc(p.image.url)}" width="${p.image.width}" height="${p.image.height}" alt="${esc(p.image.alt ?? p.image.caption ?? '')}" loading="lazy" decoding="async">${cap}</figure>`;
}

// ---------- Рыночный вид опросов и прогнозов: крупный шанс, динамика по реальным голосам, проценты на кнопках ----------

/** Маленький график динамики (0–100) — только по реальным голосам; меньше двух точек — ровная линия. */
export function sparkline(values: number[] | undefined, cls = ''): string {
  const v = values && values.length ? values : [];
  if (v.length < 2) return `<svg class="mkt__spark is-flat ${cls}" viewBox="0 0 100 32" preserveAspectRatio="none" aria-hidden="true"><line x1="0" y1="16" x2="100" y2="16"/></svg>`;
  const pts = v.map((y, i) => `${((i / (v.length - 1)) * 100).toFixed(2)},${(31 - (y / 100) * 30).toFixed(2)}`).join(' ');
  return `<svg class="mkt__spark ${cls}" viewBox="0 0 100 32" preserveAspectRatio="none" aria-hidden="true"><polyline points="${pts}"/></svg>`;
}

/** Изменение с начала голосования — только от 10 голосов: на малом числе первые ответы дают шумовые скачки. */
function delta(values: number[] | undefined): string {
  if (!values || values.length < 10) return '';
  const d = Math.round(values[values.length - 1] - values[0]);
  if (!d) return '<span class="mkt__delta">без изменений</span>';
  return `<span class="mkt__delta ${d > 0 ? 'is-up' : 'is-down'}">${d > 0 ? '▲' : '▼'} ${Math.abs(d)}</span>`;
}

/** Цвет варианта: в опросе из двух вариантов первый — «да» (зелёный), второй — «нет» (красный). */
function tone(i: number, n: number): string { return n === 2 ? (i === 0 ? 'yes' : 'no') : `m${i % 5}`; }

export function renderVoteArea(p: PublicPost, opts: CardOptions = {}): string {
  const closed = p.state === 'closed';
  if (p.type === 'POLL') {
    const r = p.result as PollResult;
    const voted = opts.voted?.optionId;
    const n = r.options.length;
    const lead = r.total ? [...r.options].sort((a, b) => b.votes - a.votes)[0] : null;
    const head = `<div class="mkt__head">
      <div class="mkt__chance">${lead ? `<b>${pct(lead.share)}</b><span>${esc(lead.text)}</span>` : '<b>—</b><span>ещё нет голосов</span>'}</div>
      ${delta(p.history)}${sparkline(p.history, n === 2 ? 'is-yes' : '')}
    </div>`;
    const state = closed ? '<p class="vote__state">Голосование закрыто</p>' : voted ? '<p class="vote__state is-ok">Голос учтён</p>' : '';
    const canVote = !closed && !voted;
    const buttons = n === 2
      ? `<div class="mkt__pair" role="group" aria-label="Варианты ответа">${r.options.map((o, i) => canVote
          ? `<button type="button" class="mkt-btn is-${tone(i, n)}" data-vote-option="${esc(o.id)}"><span>${esc(o.text)}</span><b>${pct(o.share)}</b></button>`
          : `<div class="mkt-btn is-${tone(i, n)} is-static${o.id === voted ? ' is-chosen' : ''}"><span>${esc(o.text)}${o.id === voted ? ' · ваш ответ' : ''}</span><b>${pct(o.share)}</b></div>`).join('')}</div>`
      : `<ul class="mkt__rows" role="group" aria-label="Варианты ответа">${r.options.map((o, i) => `<li class="mkt-row${o.id === voted ? ' is-chosen' : ''}">
          <span class="mkt-row__label"><i class="is-${tone(i, n)}"></i>${esc(o.text)}${o.id === voted ? ' <small>· ваш ответ</small>' : ''}</span>
          <b class="mkt-row__pct">${pct(o.share)}</b>
          ${canVote ? `<button type="button" class="mkt-row__btn" data-vote-option="${esc(o.id)}" aria-label="${esc(o.text)}">Выбрать</button>` : ''}
          <span class="mkt-row__bar" aria-hidden="true"><i class="is-${tone(i, n)}" style="--w:${o.share === null ? 0 : (o.share * 100).toFixed(2)}%"></i></span>
        </li>`).join('')}</ul>`;
    return `<div class="vote mkt" data-vote-area>${head}${state}${buttons}
      <p class="result-total">${r.total ? `Всего ${votesWord(r.total)}` : 'Пока нет голосов — ваш будет первым'}</p></div>`;
  }
  if (p.type === 'PREDICTION') {
    const r = p.result as PredictionResult;
    const mine = opts.voted?.probability;
    const outcome = p.outcome ? `<p class="vote__outcome">Итог: ${p.outcome === 'yes' ? 'событие произошло' : 'событие не произошло'}</p>` : '';
    const head = `<div class="mkt__head">
      <div class="mkt__chance">${r.average === null ? '<b>—</b><span>шанс · пока нет оценок</span>' : `<b>${Math.round(r.average)}%</b><span>шанс</span>`}</div>
      ${delta(p.history)}${sparkline(p.history, 'is-yes')}
    </div>`;
    const foot = `<p class="result-total">${r.participants ? `Средняя оценка вероятности · ${participantsWord(r.participants)}` : 'Пока нет ответов'}${mine !== undefined ? ` · ваша оценка ${mine}%` : ''}</p>`;
    if (closed || mine !== undefined) {
      return `<div class="vote mkt" data-vote-area>${head}${closed ? '<p class="vote__state">Приём прогнозов закрыт</p>' : '<p class="vote__state is-ok">Оценка принята</p>'}${foot}${outcome}</div>`;
    }
    const uid = `pr-${p.id}-${opts.mode ?? 'feed'}`;
    return `<div class="vote mkt" data-vote-area>${head}
      <form class="pred-form" data-pred-form>
        <label for="${uid}" class="pred-form__label">Ваша оценка вероятности <output data-pred-out>50%</output></label>
        <input id="${uid}" type="range" min="0" max="100" step="1" value="50" name="probability" data-pred-input>
        <div class="pred-form__ends" aria-hidden="true"><span>0% — точно нет</span><span>100% — точно да</span></div>
        <button type="submit" class="btn btn--hot">Отправить оценку</button>
      </form>
      ${foot}
    </div>`;
  }
  return '';
}

function body(p: PublicPost, opts: CardOptions): string {
  const c = p.content as Record<string, string>;
  const h = `h${opts.level ?? 3}`;
  switch (p.type) {
    case 'NEWS':
      return `${c.title ? `<${h} class="card__title">${esc(c.title)}</${h}>` : ''}<div class="card__text">${paragraphs(c.text)}</div>${renderImage(p)}`;
    case 'QUOTE':
      return `<blockquote class="quote"><p>${esc(c.quote)}</p><footer><cite>${esc(c.speaker)}</cite>${c.position ? `, <span>${esc(c.position)}</span>` : ''}</footer></blockquote>${c.context ? `<p class="quote__context">${esc(c.context)}</p>` : ''}`;
    case 'POLL':
      return `<${h} class="card__question">${esc(c.question)}</${h}>${renderVoteArea(p, opts)}`;
    case 'PREDICTION':
      return `<${h} class="card__question">${esc(c.event)}</${h}><p class="card__deadline">Приём прогнозов до ${esc(cardDateTime(p.deadline!))}</p>${renderVoteArea(p, opts)}`;
    case 'SLIDE':
      return renderImage(p);
  }
}

export function renderCard(p: PublicPost, opts: CardOptions = {}): string {
  const mode = opts.mode ?? 'feed';
  const time = mode === 'single' ? cardDateTime(p.publishedAt) : feedTime(p.publishedAt);
  const cls = ['card', `card--${p.type.toLowerCase()}`, p.isImportant ? 'is-important' : '', opts.voted ? 'is-voted' : ''].filter(Boolean).join(' ');
  const idAttr = mode === 'feed' ? ` id="post-${esc(p.id)}"` : '';
  return `<article class="${cls}"${idAttr} data-post-id="${esc(p.id)}" data-version="${p.version}" data-type="${p.type}" data-mode="${mode}" tabindex="-1">
  <header class="card__meta">
    <a class="card__time" href="${esc(p.permalink)}"><time datetime="${esc(p.publishedAt)}">${esc(time)}</time></a>
    ${p.isImportant ? importantBadge() : ''}
    ${p.author ? `<span class="card__author">${esc(p.author.name)}</span>` : ''}
    ${p.type !== 'NEWS' ? `<span class="card__type">${TYPE_LABELS[p.type]}</span>` : ''}
    ${p.topic ? `<span class="card__topic">#${esc(p.topic)}</span>` : ''}
    ${p.correctedAt ? `<span class="card__upd">Обновлено в ${esc(feedTime(p.correctedAt))}</span>` : ''}
  </header>
  <div class="card__body">${body(p, opts)}</div>
  ${mode !== 'question' ? `<footer class="card__share">
    <button type="button" class="share" data-share="link" data-url="${esc(p.permalink)}"><svg class="share__icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1"/><path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1"/></svg><span>Поделиться</span></button>
  </footer>` : ''}
</article>`;
}

/** Текст для копирования/шаринга — без HTML. */
export function shareText(p: PublicPost): string {
  const c = p.content as Record<string, string>;
  const head = p.isImportant ? '🔥 Важное. ' : '';
  switch (p.type) {
    case 'NEWS': return `${head}${c.title ? `${c.title}. ` : ''}${c.text}`;
    case 'QUOTE': return `${head}«${c.quote}» — ${c.speaker}${c.position ? `, ${c.position}` : ''}`;
    case 'POLL': return `${head}Опрос: ${c.question}`;
    case 'PREDICTION': return `${head}Прогноз: ${c.event}`;
    case 'SLIDE': return `${head}${c.caption}`;
  }
}

export function questionText(p: { type: string; content: Record<string, unknown> }): string {
  return String(p.type === 'POLL' ? p.content.question : p.content.event);
}

// ---- Экран в зале ----

const LEGEND_COLORS = 6;

export function renderScreenBar(q: ScreenQuestion): string {
  const r: Result = q.result;
  if (r.kind === 'poll') {
    const segs = r.options.map((o, i) => {
      const w = r.total ? (o.votes / r.total) * 100 : 0;
      return `<span class="sbar__seg c${i % LEGEND_COLORS}" style="--w:${w.toFixed(3)}%"><span class="sbar__seg-label">${esc(o.text)} <b>${pct(o.share)}</b></span></span>`;
    }).join('');
    const legend = `<ul class="slegend">${r.options.map((o, i) =>
      `<li class="c${i % LEGEND_COLORS}"><i aria-hidden="true"></i><span>${esc(o.text)}</span><b>${pct(o.share)}</b><small>${o.votes}</small></li>`).join('')}</ul>`;
    return `<div class="sbar sbar--poll${r.total ? '' : ' is-empty'}" role="img" aria-label="${esc(r.options.map((o) => `${o.text} ${pct(o.share)}`).join(', '))}">${segs}</div>
      ${legend}
      <p class="sfoot">${r.total ? `Всего ${answersWord(r.total)}` : 'Пока нет ответов'}</p>`;
  }
  if (r.average === null) {
    return `<div class="sbar sbar--pred is-empty"><span class="sbar__track"></span></div>
      <div class="sticks" aria-hidden="true"><i>0%</i><i>50%</i><i>100%</i></div>
      <p class="sfoot">Пока нет ответов</p>`;
  }
  const avg = Math.round(r.average);
  return `<div class="sbar sbar--pred" role="img" aria-label="Средняя оценка вероятности ${avg}%">
      <span class="sbar__track"><span class="sbar__fill" style="--w:${r.average.toFixed(3)}%"></span></span>
      <span class="sbar__mark" style="--x:${r.average.toFixed(3)}%"><b>${avg}%</b></span>
    </div>
    <div class="sticks" aria-hidden="true"><i>0%</i><i>50%</i><i>100%</i></div>
    <p class="sfoot">Средняя оценка вероятности · ${participantsWord(r.participants)}</p>`;
}

export function screenInner(s: ScreenState, qr: boolean): string {
  const q = s.question;
  if (!q) return '<div class="screen__blank" aria-label="Нет выбранного вопроса"></div>';
  return `<div class="screen__q${q.status === 'closed' ? ' is-closed' : ''}" data-qid="${esc(q.id)}">
    <h1 class="screen__text">${esc(q.text)}</h1>
    <div class="screen__result">${renderScreenBar(q)}${q.status === 'closed' ? '<p class="screen__closed">Голосование завершено · итог</p>' : ''}</div>
    ${qr && q.status === 'open' ? `<figure class="screen__qr"><div class="screen__qr-img" data-qr="${esc(q.participateUrl)}"></div><figcaption>${esc(q.participateUrl.replace(/^https?:\/\//, '').replace(/\?.*$/, ''))}</figcaption></figure>` : ''}
  </div>`;
}

